import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Play,
  Clock,
  Trash2,
  RefreshCw,
  AlertTriangle,
  Sparkles,
  FileVideo,
  CheckCircle,
  Star,
  Loader2,
  Shuffle
} from 'lucide-react';
import { toast } from 'react-toastify';

import { Video, Clip } from '../types';
import { videoApi, clipApi, formatFileSize, formatDuration, formatTimestamp } from '../services/api';
import { getDisplayTitle, isValidFileSize } from '../utils/videoUtils';
import { optimizedApi } from '../utils/apiOptimizer';
import StartupLoadingScreen from '../components/StartupLoadingScreen';
import SmartProgressIndicator from '../components/SmartProgressIndicator';
import ConfirmModal from '../components/ConfirmModal';
import { useUpload } from '../contexts/UploadContext';
import { usePolling } from '../contexts/PollingContext';
import { useClipStreaming } from '../hooks/useClipStreaming';
import { useAuth } from '../contexts/AuthContext';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import GuestSignupPrompt from '../components/GuestSignupPrompt';
import AuthPrompt from '../components/AuthPrompt';
import logger from '../lib/logger';
import thumbnailCache from '../lib/thumbnailCache';
import Tooltip from '../components/Tooltip';
import PlatformIcon from '../components/PlatformIcon';

const TAG_FRIENDLY_LABELS: Record<string, string> = {
  audio_spike: 'Audio Energy',
  speech_highlight: 'Speech Moment',
  sentiment_peak: 'Emotional Peak',
  facial_emotion: 'Visual Change',
  scene_change: 'Visual Change',
  combined: 'Multi-Signal',
};

const formatTagLabel = (tag: string): string => {
  if (TAG_FRIENDLY_LABELS[tag]) {
    return TAG_FRIENDLY_LABELS[tag];
  }

  return tag
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const VideoDetails: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const { currentUser, isGuest, canUseGuestMode: _canUseGuestMode, loading: authLoading } = useAuth();
  const { tasks, addProcessingVideo, updateTaskFromVideoStatus, getSyntheticProgress, updateSyntheticProgress, clearSyntheticProgress } = useUpload();
  const { addVideoStatusPolling, removeVideoStatusPolling } = usePolling();
  const { clips: streamedClips, isConnected: clipStreamConnected, connect: connectClipStream, disconnect: disconnectClipStream, clearClips: clearStreamedClips } = useClipStreaming();
  
  // Pre-populate state with cached data if available to eliminate loading flash
  const cachedVideo = videoId ? optimizedApi.cache.getVideoSync(videoId) : null;
  const cachedClips = videoId ? optimizedApi.cache.getClipsSync(videoId) : null;
  const cachedVideoClips = videoId ? optimizedApi.cache.getVideoClipsSync(videoId) : null;
  
  // Also check if we have video data from the dashboard cache (videos list)
  const cachedVideos = optimizedApi.cache.getVideosSync();
  const videoFromList = Array.isArray(cachedVideos) && videoId ? 
    cachedVideos.find(v => v.id === videoId) : null;
  
  const [video, setVideo] = useState<Video | null>(() => 
    (cachedVideo && typeof cachedVideo === 'object' ? cachedVideo as Video : null) ||
    (videoFromList && typeof videoFromList === 'object' ? videoFromList as Video : null)
  );
  const [clips, setClips] = useState<Clip[]>(() => {
    // Try to get clips from cache, prioritizing video clips cache over general clips cache
    // console.log(`🔍 [VideoDetails] Cache check on mount for video ${videoId}:`, {
    //   cachedVideoClips: Array.isArray(cachedVideoClips) ? `${cachedVideoClips.length} clips` : 'null',
    //   cachedClips: Array.isArray(cachedClips) ? `${cachedClips.length} clips` : 'null',
    //   videoFromListClips: videoFromList?.clips ? `${videoFromList.clips.length} clips` : 'null'
    // });
    
    if (Array.isArray(cachedVideoClips) && cachedVideoClips.length > 0) {
      // console.log(`✅ [VideoDetails] Using ${cachedVideoClips.length} cached video clips`);
      return cachedVideoClips;
    }
    
    if (Array.isArray(cachedClips) && cachedClips.length > 0) {
      // console.log(`✅ [VideoDetails] Using ${cachedClips.length} cached clips`);
      return cachedClips;
    }
    
    // Fallback: check if video has clips in the videos list cache
    if (videoFromList && Array.isArray(videoFromList.clips) && videoFromList.clips.length > 0) {
      // console.log(`✅ [VideoDetails] Using ${videoFromList.clips.length} clips from video list cache`);
      return videoFromList.clips;
    }
    
    // console.log(`❌ [VideoDetails] No cached clips found, starting with empty array`);
    return [];
  });
  const [loading, setLoading] = useState(!cachedVideo && !videoFromList); // Only show loading if no cached data
  
  // Debug log for loading state changes
  useEffect(() => {
    // console.log(`🔍 [DEBUG] LOADING STATE CHANGED TO: ${loading}`);
    if (loading) {
      // console.trace(`🔍 [DEBUG] Loading set to TRUE - stack trace:`);
    } else {
      // console.log(`🔍 [DEBUG] Loading set to   FALSE - component should render content now!`);
    }
  }, [loading]);
  const [clipsLoading, setClipsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showTimeline, setShowTimeline] = useState(false);
  const [autoAnalyzing, setAutoAnalyzing] = useState(false);
  const [_useProxy, _setUseProxy] = useState(false);
  const [clipThumbnails, setClipThumbnails] = useState<Record<string, string>>({});
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });
  const [clipLoadError, setClipLoadError] = useState<string | null>(null);
  const [clipRetryCount, setClipRetryCount] = useState(0);
  const [lastClipLoadAttempt, setLastClipLoadAttempt] = useState<number>(0);
  const [rateLimitCountdown, setRateLimitCountdown] = useState<number>(0);
  const [showGuestSignupPrompt, setShowGuestSignupPrompt] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);

  // Fallback thumbnail state for videos without a stored thumbnail_url
  const [videoThumbUrl, setVideoThumbUrl] = useState<string | null>(null);
  const [videoThumbError, setVideoThumbError] = useState(false);
  const lastVideoThumbUrlRef = useRef<string | null>(null);

  // Use refs to prevent infinite loops
  const hasAddedProcessingTaskRef = useRef(false);
  const pollingStartedRef = useRef(false);
  const completionToastShownRef = useRef(false);
  const analysisCompletedRef = useRef(false);
  const _pollingIntervalRef = useRef(1000); // Default polling interval
  const lastWebSocketDecisionRef = useRef<{ shouldConnect: boolean; timestamp: number; connectionTime?: number }>({ shouldConnect: false, timestamp: 0 });

  // Add ref to track if component is mounted
  const isMountedRef = useRef(true);
  // Add mount counter for debugging
  const mountCountRef = useRef(0);
  
  // Enable keyboard shortcuts for video controls
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  useKeyboardShortcuts({ 
    videoElement: videoElementRef.current,
    onPlayPause: () => {
      // Find any video element on the page and control it
      const videoElement = document.querySelector('video') as HTMLVideoElement;
      if (videoElement) {
        if (videoElement.paused) {
          videoElement.play();
        } else {
          videoElement.pause();
        }
      }
    }
  });
  
  useEffect(() => {
    mountCountRef.current++;
    // console.log(`🔍 [DEBUG] VideoDetails mounted ${mountCountRef.current} times for video ${videoId}`);
    
    // Only scroll to top when the video ID changes (actual navigation)
    window.scrollTo(0, 0);
  }, [videoId]);

  // Generate fallback thumbnail if missing
  useEffect(() => {
    // Clean up previous blob URL if any
    if (lastVideoThumbUrlRef.current && lastVideoThumbUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(lastVideoThumbUrlRef.current);
      lastVideoThumbUrlRef.current = null;
    }

    setVideoThumbUrl(null);
    setVideoThumbError(false);

    const fetchThumb = async () => {
      try {
        if (!video || video.thumbnail_url) return; // nothing to do if we already have one
        const thumbUrl = await videoApi.getVideoThumbnail(video.id, 5);
        if (!isMountedRef.current) {
          // If unmounted, revoke immediately
          if (thumbUrl.startsWith('blob:')) URL.revokeObjectURL(thumbUrl);
          return;
        }
        setVideoThumbUrl(thumbUrl);
        lastVideoThumbUrlRef.current = thumbUrl;
      } catch (e) {
        setVideoThumbError(true);
      }
    };

    fetchThumb();

    return () => {
      if (lastVideoThumbUrlRef.current && lastVideoThumbUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(lastVideoThumbUrlRef.current);
        lastVideoThumbUrlRef.current = null;
      }
    };
  }, [video]);
  
  const loadVideoDetails = useCallback(async () => {
    if (!videoId || !isMountedRef.current) {
      // console.log(`🔍 [DEBUG] Skipping loadVideoDetails - videoId: ${videoId}, mounted: ${isMountedRef.current}`);
      return;
    }
    
    // console.log(`🔍 [DEBUG] loadVideoDetails called for video ${videoId}`);
    
    try {
      
      // Only set loading if component is still mounted AND we don't have cached data
      if (isMountedRef.current && !cachedVideo && !videoFromList) {
        setLoading(true);
        // console.log(`🔍 [DEBUG] Set loading to TRUE for video ${videoId}`);
        setError(null);
      }
      
      // console.log(`🔄 [VideoDetails] Loading video details for ${videoId}`);
      
      // Prefetch dashboard data for smooth navigation back to dashboard
      optimizedApi.prefetch.dashboardData();
      
      // Reset completion toast flag for new video loads
      completionToastShownRef.current = false;
      analysisCompletedRef.current = false;
      
      // Reset clips state when loading new video
      if (isMountedRef.current) {
        setClips([]);
        setClipLoadError(null);
        setClipRetryCount(0);
        setLastClipLoadAttempt(0);
        setRateLimitCountdown(0);
      }
      
      // Load video details using optimized API with caching
      let videoData;
      try {
        videoData = await optimizedApi.getVideo(videoId);
      } catch (initialError: any) {
        // If we get a 404, it might be a temporary issue or race condition on slow connections.
        // Wait a few seconds and retry once before showing the error.
        if (initialError?.response?.status === 404) {
          // console.log(`⚠️ [VideoDetails] 404 encountered, waiting and retrying...`);
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          if (!isMountedRef.current) return;
          
          // Force invalidate cache just in case
          optimizedApi.cache.invalidate(`video_${videoId}`);
          videoData = await optimizedApi.getVideo(videoId);
        } else {
          throw initialError;
        }
      }
      
      // Only update state if component is still mounted
      if (!isMountedRef.current) {
        // console.log(`🔍 [DEBUG] Component unmounted, skipping state updates for video ${videoId}`);
        return;
      }
      
      setVideo(videoData);
      // console.log(`🔍 [DEBUG] Loaded video data:`, videoData.status, videoData.filename);
      
      // console.log(`📹 [VideoDetails] Loaded video data:`, {
      //   id: videoData.id,
      //   status: videoData.status,
      //   filename: videoData.filename,
      //   error_message: videoData.error_message,
      //   highlights_count: videoData.highlights?.length || 0,
      //   clips_count: videoData.clips?.length || 0
      // });
      
      // If this is a placeholder (import_pending), set up aggressive polling to detect when real data arrives
      if (videoData.filename?.startsWith('import_pending_') || videoData.metadata?.duration === 0) {
        // console.log('🔄 Detected placeholder video, starting aggressive polling for import completion');
        const pollInterval = setInterval(async () => {
          try {
            // Invalidate cache and fetch fresh data
            optimizedApi.cache.invalidate(`video_${videoId}`);
            const freshData = await optimizedApi.getVideo(videoId);
            if (freshData && !freshData.filename?.startsWith('import_pending_') && freshData.metadata?.duration > 0) {
              // Real data has arrived!
              // console.log('✅ Import completed, real video data received');
              clearInterval(pollInterval);
              setVideo(freshData);
            }
          } catch (err) {
            // Ignore errors during polling
          }
        }, 2000); // Poll every 2 seconds
        
        // Clean up polling after 5 minutes max
        setTimeout(() => clearInterval(pollInterval), 300000);
      }
      
      // If video is already analyzed, load clips immediately but DON'T show completion toast
      if (videoData.status === 'analyzed') {
        // console.log(`🔍 [DEBUG] Video is ANALYZED, loading clips immediately`);
        // console.log(`✅ [VideoDetails] Video is analyzed, loading clips immediately`);
        // Mark as already completed to prevent toast from showing
        completionToastShownRef.current = true;
        analysisCompletedRef.current = true;
        
        try {
          const clipsData = await optimizedApi.getVideoClips(videoId);
          setClips(clipsData);
          // console.log(`🔍 [DEBUG] Loaded ${clipsData.length} clips for analyzed video`);
          // console.log(`📥 [VideoDetails] Loaded ${clipsData.length} clips for analyzed video`);
          
          // Preload thumbnails for clips
          preloadClipThumbnails(clipsData);
        } catch (clipError) {
          console.error('❌ [VideoDetails] Error loading clips:', clipError);
          // Don't block the page load, clips can be retried
        }
      } else if (videoData.status === 'processing') {
        // console.log(`🔍 [DEBUG] Video is PROCESSING, will start polling`);
        // console.log(`⏳ [VideoDetails] Video is processing, will start polling`);
        // Reset flags so toast can show when analysis completes
        completionToastShownRef.current = false;
        analysisCompletedRef.current = false;
      } else {
        // console.log(`🔍 [DEBUG] Video status: ${videoData.status}`);
        // console.log(`📝 [VideoDetails] Video status: ${videoData.status}`);
        // For uploaded videos, reset flags so toast can show when analysis starts and completes
        completionToastShownRef.current = false;
        analysisCompletedRef.current = false;
      }
      
      setLoading(false); // Video is loaded, hide main loading screen immediately
      // console.log(`🔍 [DEBUG] Set loading to FALSE for video ${videoId} - page should show now!`);
      
      // Reset retry count on success
      setRetryCount(0);
      
    } catch (error: any) {
      console.error('❌ [VideoDetails] Error loading video details:', error);
      
      // Don't update state if component is unmounted
      if (!isMountedRef.current) {
        // console.log(`🔍 [DEBUG] Component unmounted, skipping error handling for video ${videoId}`);
        return;
      }
      
      // Determine error type based on error details
      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout') || error?.message?.includes('aborted')) {
        setError('Request timed out while loading video details. This usually happens with large videos that are still processing.');
        toast.error('Request timed out. Your video might still be processing.');
      } else if (error?.response?.status === 404) {
        setError('Video not found. It may have been deleted or the link is incorrect.');
        toast.error('Video not found');
      } else if (error?.code === 'ERR_NETWORK' || !navigator.onLine) {
        setError('Network connection problem. Please check your internet connection.');
        toast.error('Network connection error');
      } else {
        setError('Failed to load video details. Please try again.');
        toast.error('Failed to load video details');
      }
      setLoading(false);
      // console.log(`🔍 [DEBUG] Set loading to FALSE due to error for video ${videoId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  const _loadClips = useCallback(async () => {
    if (!videoId || clips.length > 0) return;
    
    // Check if clips are available in cache first to avoid loading state
    const cachedVideoClips = optimizedApi.cache.getVideoClipsSync(videoId);
    const cachedClips = optimizedApi.cache.getClipsSync(videoId);
    
    // console.log(`🔍 [VideoDetails] loadClips cache check for video ${videoId}:`, {
    //   cachedVideoClips: Array.isArray(cachedVideoClips) ? `${cachedVideoClips.length} clips` : 'null',
    //   cachedClips: Array.isArray(cachedClips) ? `${cachedClips.length} clips` : 'null',
    //   currentClipsLength: clips.length
    // });
    
    const availableClips = (Array.isArray(cachedVideoClips) && cachedVideoClips.length > 0) ? cachedVideoClips :
                          (Array.isArray(cachedClips) && cachedClips.length > 0) ? cachedClips : null;
    
    if (availableClips) {
      // console.log(`✅ [VideoDetails] Using ${availableClips.length} cached clips, skipping loading state`);
      // Use cached clips immediately without showing loading
      setClips(availableClips);
      setClipLoadError(null);
      setClipRetryCount(0);
      
      // Preload thumbnails for cached clips
      preloadClipThumbnails(availableClips);
      
      // Pre-populate individual clip caches for instant navigation
      availableClips.forEach(clip => {
        optimizedApi.prefetch.clipDetails(clip.id);
      });
      
      return;
    }
    
    // console.log(`❌ [VideoDetails] No cached clips found, showing loading state and fetching from API`);
    
    setClipsLoading(true);
    try {
      const clipsData = await optimizedApi.getVideoClips(videoId);
      // console.log(`📥 [VideoDetails] Loaded ${clipsData.length} clips from API for video ${videoId}, caching for future use`);
      setClips(clipsData);
      setClipLoadError(null); // Clear any previous errors
      setClipRetryCount(0); // Reset retry count on success
      
      // Show guest signup prompt if this is a guest user and clips were generated
      if (!currentUser && clipsData.length > 0 && !showGuestSignupPrompt) {
        // Small delay to let user see the clips first
        setTimeout(() => {
          setShowGuestSignupPrompt(true);
        }, 2000);
      }
      
      // Preload thumbnails for clips
      preloadClipThumbnails(clipsData);
      
      // Pre-populate individual clip caches for instant navigation
      clipsData.forEach(clip => {
        optimizedApi.prefetch.clipDetails(clip.id);
      });
      
      // console.log(`✅ [VideoDetails] Successfully loaded ${clipsData.length} clips for video ${videoId}`);
    } catch (clipError: any) {
      logger.error('Error loading clips:', clipError);
      // Don't set main error state for clip loading failures
    } finally {
      setClipsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, clips.length, currentUser, showGuestSignupPrompt]);

  useEffect(() => {
    // console.log(`🔍 [DEBUG] useEffect #1 triggered - videoId: ${videoId}, authLoading: ${authLoading}`);
    // Wait for auth to load before fetching video to ensure proper authentication
    if (videoId && !authLoading) {
      // console.log(`🔍 [DEBUG] useEffect #1 calling loadVideoDetails for video: ${videoId}`);
      
      // Ensure component is marked as mounted for this video
      isMountedRef.current = true;
      
      // Reset polling flag when video changes to allow polling to restart
      pollingStartedRef.current = false;
      // console.log('[VideoDetails] Reset polling flag for video change');
      
      // CRITICAL: Clean up any existing polling before starting new
      // console.log(`🔍 [DEBUG] Cleaning up existing polling before loading video ${videoId}`);
      removeVideoStatusPolling(videoId);
      
      loadVideoDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, authLoading]); // Wait for auth to load before fetching video

  // Consolidated WebSocket connection management for live clip updates
  useEffect(() => {
    // console.log(`🔍 [DEBUG] useEffect #2 (WebSocket) triggered - videoId: ${videoId}, video status: ${video?.status}, clips: ${clips.length}, streamedClips: ${streamedClips.length}`);
    if (!videoId || !video) return;

    const progressMatch = video.error_message?.match(/(\d+(?:\.\d+)?)%/);
    const progress = progressMatch ? parseFloat(progressMatch[1]) : 0;
    const isGeneratingClips = !!(video.error_message?.toLowerCase().includes('generating clip') || 
                                 video.error_message?.toLowerCase().includes('clip generation'));
    
    // Determine if we should connect based on video status and progress
    let shouldConnect = false;
    
    if (video.status === 'processing') {
      // Connect if we're in clip generation phase (25% or higher) OR if clips are being generated
      shouldConnect = progress >= 25 || isGeneratingClips;
    } else if (video.status === 'analyzed') {
      // For analyzed videos, stay connected if:
      // 1. We have fewer clips than expected (based on highlights)
      // 2. Or we have no clips yet and there should be some
      // 3. Or we recently connected (minimum connection time)
      const currentClipCount = clips.length + streamedClips.length;
      const expectedClipCount = video.highlights?.length || 0;
      const hasNoClips = currentClipCount === 0 && expectedClipCount > 0;
      const expectingMoreClips = currentClipCount < expectedClipCount && expectedClipCount > 0;
      
      // Minimum connection time: stay connected for at least 30 seconds after connecting
      const connectionTime = lastWebSocketDecisionRef.current.connectionTime || 0;
      const timeSinceConnection = Date.now() - connectionTime;
      const withinMinConnectionTime = connectionTime > 0 && timeSinceConnection < 30000; // 30 seconds
      
      shouldConnect = hasNoClips || expectingMoreClips || withinMinConnectionTime;
    }
    
    // IMPORTANT: Fix page load detection to avoid infinite reconnection loops
    const isPageLoad = !lastWebSocketDecisionRef.current.timestamp || 
                       (Date.now() - lastWebSocketDecisionRef.current.timestamp > 30000); // Consider page load if > 30 seconds since last decision
    
    // Only make connection changes when there's a real decision change or on page load
    const lastDecision = lastWebSocketDecisionRef.current.shouldConnect;
    const decisionChanged = shouldConnect !== lastDecision;
    
    if ((shouldConnect && !clipStreamConnected) || (isPageLoad && shouldConnect)) {
      // console.log(`🔌 [VideoDetails] Connecting to clip stream (progress: ${progress}%, generating: ${isGeneratingClips}, status: ${video.status}, clips: ${clips.length}/${video.highlights?.length || 0}, pageLoad: ${isPageLoad})`);
      // Clear any existing streamed clips before connecting to start fresh
      clearStreamedClips();
      connectClipStream(videoId);
      lastWebSocketDecisionRef.current = { 
        shouldConnect: true, 
        timestamp: Date.now(),
        connectionTime: Date.now() // Track when we connected
      };
    } else if (!shouldConnect && clipStreamConnected && decisionChanged) {
      // Additional safeguard: don't disconnect if we just connected recently (minimum 10 seconds)
      const connectionTime = lastWebSocketDecisionRef.current.connectionTime || 0;
      const timeSinceConnection = Date.now() - connectionTime;
      
      if (timeSinceConnection >= 10000) { // At least 10 seconds since connection
        // console.log(`🔌 [VideoDetails] Disconnecting clip stream (progress: ${progress}%, status: ${video.status}, clips: ${clips.length}/${video.highlights?.length || 0}, connected for: ${Math.round(timeSinceConnection/1000)}s)`);
        disconnectClipStream();
        lastWebSocketDecisionRef.current = { shouldConnect: false, timestamp: Date.now() };
      } else {
        // console.log(`🔌 [VideoDetails] Delaying disconnect - only connected for ${Math.round(timeSinceConnection/1000)}s (minimum 10s required)`);
      }
    }
    
    // CRITICAL: Add aggressive fallback polling when processing and WebSocket fails
    if (video.status === 'processing' && shouldConnect && !clipStreamConnected) {
      // console.log(`🔄 [VideoDetails] WebSocket failed but clips are generating. Starting aggressive fallback polling...`);
      
      const fallbackPollInterval = setInterval(async () => {
        try {
          // console.log(`🔄 [VideoDetails] Fallback polling: checking for new clips...`);
          const clipsData = await optimizedApi.getVideoClips(videoId);
          const currentTotal = clips.length + streamedClips.length;
          
          if (clipsData.length > currentTotal) {
            // console.log(`✅ [VideoDetails] Fallback polling found ${clipsData.length - currentTotal} new clips!`);
            setClips(clipsData);
            preloadClipThumbnails(clipsData);
            
            // Invalidate frontend cache when new clips are detected
            optimizedApi.cache.invalidateVideos(); // Invalidates videos, analytics, and stats
            optimizedApi.cache.invalidate('clips'); // Invalidate clips cache
          }
        } catch (error) {
          console.error('❌ [VideoDetails] Fallback polling failed:', error);
        }
      }, 45000); // Poll every 45 seconds when WebSocket fails (drastically reduced frequency)
      
      // Clear fallback polling when WebSocket connects or status changes
      const clearFallbackPolling = () => clearInterval(fallbackPollInterval);
      
      // Set up cleanup
      const timeoutId = setTimeout(() => {
        if (!clipStreamConnected) {
          clearFallbackPolling();
        }
      }, 15000); // Stop fallback after 15 seconds to reduce spam
      
      return () => {
        clearFallbackPolling();
        clearTimeout(timeoutId);
      };
    }
    
    // Don't cleanup on unmount - let the hook handle cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, video?.status, video?.error_message, video?.highlights?.length, clips.length, streamedClips.length, clipStreamConnected]); // ✅ Removed function dependencies to prevent re-renders

  // CONSOLIDATED: Single polling system that handles both initial load and resume scenarios
  useEffect(() => {
    if (video && video.status === 'processing' && videoId && !pollingStartedRef.current) {
      // console.log('🔄 [VideoDetails] Starting/resuming status polling for processing video');
      pollingStartedRef.current = true;
      
      // Initialize synthetic progress for this video to start at current progress
      const currentProgress = video.error_message?.match(/(\d+(?:\.\d+)?)%/);
      const initialProgress = currentProgress ? parseFloat(currentProgress[1]) : 5;
      updateSyntheticProgress(videoId, initialProgress, 'Resuming video analysis...');
      
      const handleStatusUpdate = async (statusData: any) => {
        // console.log('📊 [VideoDetails] Status update received:', statusData);
        
        // Parse progress from error_message to get backend progress
        const parseProgress = (errorMessage: string): { progress: number; message: string } => {
          if (!errorMessage) {
            return { progress: 5, message: 'Processing...' };
          }
          
          const progressMatch = errorMessage.match(/^(\d+(?:\.\d+)?)%\s*[-–—]\s*(.+)$/);
          if (progressMatch) {
            return { progress: parseFloat(progressMatch[1]), message: progressMatch[2].trim() };
          }
          
          const progressMatch2 = errorMessage.match(/^(\d+(?:\.\d+)?)%\s+(.+)$/);
          if (progressMatch2) {
            return { progress: parseFloat(progressMatch2[1]), message: progressMatch2[2].trim() };
          }
          
          const anyProgressMatch = errorMessage.match(/(\d+(?:\.\d+)?)%/);
          if (anyProgressMatch) {
            return { progress: parseFloat(anyProgressMatch[1]), message: errorMessage };
          }
          
          // Special handling for common status messages without percentages
          const lowerMessage = errorMessage.toLowerCase();
          if (lowerMessage.includes('complete') || lowerMessage.includes('finished') || lowerMessage.includes('done')) {
            return { progress: 100, message: errorMessage };
          }
          if (lowerMessage.includes('starting') || lowerMessage.includes('initializing')) {
            return { progress: 5, message: errorMessage };
          }
          if (lowerMessage.includes('analyzing') || lowerMessage.includes('processing')) {
            return { progress: 25, message: errorMessage };
          }
          if (lowerMessage.includes('generating') || lowerMessage.includes('creating')) {
            return { progress: 75, message: errorMessage };
          }
          
          return { progress: 5, message: errorMessage || 'Processing...' };
        };
        
        const { progress: backendProgress, message: progressMessage } = parseProgress(statusData.error_message || '');
        // console.log(`🎯 [VideoDetails] Parsed progress: ${backendProgress}% - ${progressMessage}`);
        
        // CRITICAL: Always update synthetic progress AND local state
        updateSyntheticProgress(videoId, backendProgress, progressMessage);
        
        // CRITICAL: Force update the local video state immediately with latest status
        setVideo(prevVideo => {
          if (!prevVideo) return prevVideo;
          
          const updatedVideo = {
            ...prevVideo,
            status: statusData.status as any,
            error_message: statusData.error_message || `${backendProgress}% - ${progressMessage}`,
            highlights_count: statusData.highlights_count || prevVideo.highlights?.length || 0,
            clips_count: statusData.clips_count || prevVideo.clips?.length || 0
          };
          
          // console.log(`🔄 [VideoDetails] FORCE UPDATED video state:`, {
          //   id: updatedVideo.id,
          //   status: updatedVideo.status,
          //   error_message: updatedVideo.error_message,
          //   clips_count: updatedVideo.clips_count
          // });
          return updatedVideo;
        });
        
        // Update upload task progress (for mini widget)
        updateTaskFromVideoStatus(videoId, statusData);
        
        // CRITICAL: Check if we're missing clips and need to refresh
        const expectedClips = statusData.clips_count || 0;
        // Don't double-count clips that are both in clips[] and streamedClips[]
        // Use a Set to deduplicate by clip ID
        const allClipIds = new Set([
          ...clips.map(c => c.id),
          ...streamedClips.map(c => c.id)
        ]);
        const currentClips = allClipIds.size;
        
        if (expectedClips > currentClips && expectedClips > 0) {
          // console.log(`🔄 [VideoDetails] Backend reports ${expectedClips} clips but we only have ${currentClips}. Refreshing clips...`);
          // Force reload clips from API to catch any missed by WebSocket
          try {
            const clipsData = await optimizedApi.getVideoClips(videoId);
            if (clipsData.length > currentClips) {
              // console.log(`✅ [VideoDetails] Found ${clipsData.length - currentClips} missing clips, updating state`);
              setClips(clipsData);
              preloadClipThumbnails(clipsData);
            }
          } catch (clipError) {
            console.error('❌ [VideoDetails] Error refreshing clips:', clipError);
          }
        }
        
        // CRITICAL: If analysis is complete, stop polling and load clips immediately
        if (statusData.status === 'analyzed') {
          // console.log('✅ [VideoDetails] Analysis completed - STOPPING POLLING and loading clips...');
          
          // Stop polling immediately and ensure no duplicate polling restarts
          removeVideoStatusPolling(videoId);
          pollingStartedRef.current = false;
          setAutoAnalyzing(false);
          clearSyntheticProgress(videoId);
          
          // Mark analysis as completed to prevent restart
          analysisCompletedRef.current = true;
          
          // Force update video state to completed IMMEDIATELY
          setVideo(prevVideo => {
            if (!prevVideo) return prevVideo;
            return {
              ...prevVideo,
              status: 'analyzed',
              error_message: '100% - Analysis complete'
            };
          });
          
          // Load clips immediately
          if (!analysisCompletedRef.current) {
            analysisCompletedRef.current = true;
            // console.log('📥 [VideoDetails] Loading clips immediately...');
            
            try {
              const clipsData = await optimizedApi.getVideoClips(videoId);
              setClips(clipsData);
              // console.log(`✅ [VideoDetails] Loaded ${clipsData.length} clips after completion`);
              
              // Preload thumbnails for clips
              preloadClipThumbnails(clipsData);
              
              // Show success toast
              if (!completionToastShownRef.current && clipsData.length > 0) {
                completionToastShownRef.current = true;
                toast.success(`🎉 Analysis complete! Generated ${clipsData.length} clips.`);
              }
            } catch (clipError) {
              console.error('❌ [VideoDetails] Error loading clips:', clipError);
              // Force page reload as fallback
              window.location.reload();
            }
          }
        }
      };

      // console.log('🚀 [VideoDetails] Adding video status polling with HIGH priority');
      addVideoStatusPolling(videoId, handleStatusUpdate, 'high');
      
      // Mark as already added to prevent auto-analysis from running
      hasAddedProcessingTaskRef.current = true;
      setAutoAnalyzing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video, videoId]); // ✅ Removed function dependencies to prevent re-renders

  // Load clips when video status changes to analyzed or when explicitly requested
  useEffect(() => {
    // console.log(`🔍 [DEBUG] useEffect #3 (clips loading) triggered - status: ${video?.status}, clips: ${clips.length}, clipsLoading: ${clipsLoading}, clipLoadError: ${clipLoadError}`);
    if (video?.status === 'analyzed' && clips.length === 0 && !clipsLoading && !clipLoadError) {
      // Implement exponential backoff for rate limiting
      const now = Date.now();
      const timeSinceLastAttempt = now - lastClipLoadAttempt;
      const minRetryDelay = Math.min(1000 * Math.pow(2, clipRetryCount), 30000); // Max 30 seconds
      
      // Don't retry too quickly, especially if we've been rate limited
      if (timeSinceLastAttempt < minRetryDelay) {
        // console.log(`⏳ [VideoDetails] Waiting ${minRetryDelay - timeSinceLastAttempt}ms before next clip load attempt`);
        return;
      }
      
      // Call loadClips directly to avoid dependency issues
      const loadClipsData = async () => {
        if (!videoId) return;
        
        // Check if clips are available in cache first to avoid loading state
        const cachedVideoClips = optimizedApi.cache.getVideoClipsSync(videoId);
        const cachedClips = optimizedApi.cache.getClipsSync(videoId);
        
        const availableClips = (Array.isArray(cachedVideoClips) && cachedVideoClips.length > 0) ? cachedVideoClips :
                              (Array.isArray(cachedClips) && cachedClips.length > 0) ? cachedClips : null;
        
        if (availableClips) {
          // Use cached clips immediately without showing loading
          setClips(availableClips);
          setClipLoadError(null);
          setClipRetryCount(0);
          
          // Preload thumbnails for cached clips
          preloadClipThumbnails(availableClips);
          
          return;
        }
        
        setClipsLoading(true);
        setLastClipLoadAttempt(Date.now());
        
        try {
          const clipsData = await optimizedApi.getVideoClips(videoId);
          setClips(clipsData);
          setClipLoadError(null); // Clear any previous errors
          setClipRetryCount(0); // Reset retry count on success
          // console.log(`✅ [VideoDetails] Successfully loaded ${clipsData.length} clips for video ${videoId}`);
          
          // Preload thumbnails for clips
          preloadClipThumbnails(clipsData);
        } catch (clipError: any) {
          logger.error('Error loading clips:', clipError);
          
          // Handle different types of errors
          if (clipError.response?.status === 429) {
            const retryAfter = clipError.response?.headers?.['retry-after'];
            const retryAfterData = clipError.response?.data?.retry_after;
            const waitTimeSeconds = retryAfter ? parseInt(retryAfter) : 
                                  retryAfterData ? retryAfterData : 
                                  Math.min(5 * Math.pow(2, clipRetryCount), 60);
            
            setRateLimitCountdown(waitTimeSeconds);
            setClipLoadError(`Rate limited. Retrying in ${waitTimeSeconds} seconds...`);
            setClipRetryCount(prev => prev + 1);
            
          } else if (clipError.response?.status === 404) {
            setClipLoadError('No clips found for this video.');
          } else if (clipError.code === 'ERR_NETWORK') {
            setClipLoadError('Network error. Please check your connection.');
            setClipRetryCount(prev => prev + 1);
          } else {
            setClipLoadError('Failed to load clips. Please try again.');
            setClipRetryCount(prev => prev + 1);
          }
          
          // Don't set main error state for clip loading failures
        } finally {
          setClipsLoading(false);
        }
      };
      
      loadClipsData();
    }
    
    // CRITICAL: Also load clips for processing videos to catch any that were generated while user was away
    if (video?.status === 'processing' && clips.length === 0 && !clipsLoading && !clipLoadError) {
      // console.log('🔄 [VideoDetails] Processing video detected, checking for existing clips...');
      
      const loadExistingClips = async () => {
        if (!videoId) return;
        
        // Check if clips are available in cache first to avoid loading state
        const cachedVideoClips = optimizedApi.cache.getVideoClipsSync(videoId);
        const cachedClips = optimizedApi.cache.getClipsSync(videoId);
        
        const availableClips = (Array.isArray(cachedVideoClips) && cachedVideoClips.length > 0) ? cachedVideoClips :
                              (Array.isArray(cachedClips) && cachedClips.length > 0) ? cachedClips : null;
        
        if (availableClips) {
          // Use cached clips immediately without showing loading
          setClips(availableClips);
          setClipLoadError(null);
          setClipRetryCount(0);
          
          // Preload thumbnails for cached clips
          preloadClipThumbnails(availableClips);
          
          return;
        }
        
        setClipsLoading(true);
        setLastClipLoadAttempt(Date.now());
        
        try {
          const clipsData = await optimizedApi.getVideoClips(videoId);
          if (clipsData.length > 0) {
            setClips(clipsData);
            setClipLoadError(null);
            setClipRetryCount(0);
            // console.log(`✅ [VideoDetails] Found ${clipsData.length} existing clips for processing video`);
            
            // Preload thumbnails for clips
            preloadClipThumbnails(clipsData);
          }
        } catch (clipError: any) {
          // For processing videos, don't show errors for missing clips (404 is expected)
          if (clipError.response?.status !== 404) {
            logger.error('Error loading existing clips for processing video:', clipError);
          }
        } finally {
          setClipsLoading(false);
        }
      };
      
      loadExistingClips();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video?.status, clips.length, clipsLoading, videoId]); // ✅ Removed self-updating dependencies to prevent infinite loops

  // Force reload clips when video analysis completes
  useEffect(() => {
    if (video?.status === 'analyzed' && !analysisCompletedRef.current) {
      // console.log('🔄 [VideoDetails] Video analysis completed, forcing clips reload');
      analysisCompletedRef.current = true;
      
      // Clear existing clips and force reload
      setClips([]);
      setClipLoadError(null);
      setClipRetryCount(0);
      setLastClipLoadAttempt(0);
      setRateLimitCountdown(0);
    }
  }, [video?.status]);

  // Auto-generate clips for uploaded videos without clips
  useEffect(() => {
    // console.log(`🔍 [DEBUG] useEffect #4 (auto analysis) triggered - status: ${video?.status}, clips: ${clips.length}, autoAnalyzing: ${autoAnalyzing}, videoId: ${videoId}`);
    const autoStartAnalysis = async () => {
      if (!videoId || !video || autoAnalyzing || hasAddedProcessingTaskRef.current) return;
      
      // Additional safety check: don't start if video is already processing
      if (video.status === 'processing') {
        // console.log('Video is already processing, skipping auto-analysis');
        return;
      }
      
      setAutoAnalyzing(true);
      hasAddedProcessingTaskRef.current = true;
      
      try {
        // Check if a processing task already exists for this video to prevent duplicates
        const existingTask = tasks.find(task => 
          task.videoId === videoId && 
          (task.status === 'uploading' || task.status === 'analyzing' || 
           (task.status === 'completed' && (Date.now() - task.startTime.getTime()) < 60000))
        );
        
        let _taskId: string;
        if (existingTask) {
          // console.log(`🔄 [VideoDetails] Using existing processing task: ${existingTask.id} for video: ${videoId}`);
          _taskId = existingTask.id;
        } else {
          // Also check localStorage to prevent cross-component duplicates
          const recentTasksKey = 'recent_processing_tasks';
          const recentTasks = JSON.parse(localStorage.getItem(recentTasksKey) || '{}');
          const now = Date.now();
          
          if (recentTasks[videoId] && (now - recentTasks[videoId]) < 300000) {
            // console.log(`🔄 [VideoDetails] Recent task found in localStorage for ${videoId}, skipping duplicate creation`);
            _taskId = `existing_${videoId}`;
          } else {
            // Add this video to the upload widget for progress tracking BEFORE starting analysis
            // This ensures the task is created even if the user navigates away
            _taskId = addProcessingVideo(videoId, video.filename, video.metadata?.file_size || 0);
            // console.log(`📋 [VideoDetails] Created new processing task: ${_taskId} for video: ${videoId}`);
          }
        }
        
        const analysisRequest = {
          video_id: videoId,
          sensitivity: 0.3,
          min_clip_duration: 10,
          max_clip_duration: 90,
          padding_before: 3,
          padding_after: 3,
          enable_audio_analysis: true,
          enable_face_detection: true,
          enable_emotion_detection: true
        };
        
        await videoApi.analyzeVideo(analysisRequest, { silent: true });
        
        // Update video status to processing
        setVideo(prevVideo => {
          if (!prevVideo) return prevVideo;
          return { ...prevVideo, status: 'processing' };
        });
        
        // Start unified polling for this video
        const handleStatusUpdate = async (statusData: any) => {
          // console.log('📊 [VideoDetails] Auto-analysis status update:', statusData);
          
          // Parse progress from error_message to get backend progress
          const parseProgress = (errorMessage: string): { progress: number; message: string } => {
            if (!errorMessage) {
              return { progress: 5, message: 'Processing...' };
            }
            
            const progressMatch = errorMessage.match(/^(\d+(?:\.\d+)?)%\s*[-–—]\s*(.+)$/);
            if (progressMatch) {
              return { progress: parseFloat(progressMatch[1]), message: progressMatch[2].trim() };
            }
            
            const progressMatch2 = errorMessage.match(/^(\d+(?:\.\d+)?)%\s+(.+)$/);
            if (progressMatch2) {
              return { progress: parseFloat(progressMatch2[1]), message: progressMatch2[2].trim() };
            }
            
            const anyProgressMatch = errorMessage.match(/(\d+(?:\.\d+)?)%/);
            if (anyProgressMatch) {
              return { progress: parseFloat(anyProgressMatch[1]), message: errorMessage };
            }
            
            // Special handling for common status messages without percentages
            const lowerMessage = errorMessage.toLowerCase();
            if (lowerMessage.includes('complete') || lowerMessage.includes('finished') || lowerMessage.includes('done')) {
              return { progress: 100, message: errorMessage };
            }
            if (lowerMessage.includes('starting') || lowerMessage.includes('initializing')) {
              return { progress: 5, message: errorMessage };
            }
            if (lowerMessage.includes('analyzing') || lowerMessage.includes('processing')) {
              return { progress: 25, message: errorMessage };
            }
            if (lowerMessage.includes('generating') || lowerMessage.includes('creating')) {
              return { progress: 75, message: errorMessage };
            }
            
            return { progress: 5, message: errorMessage || 'Processing...' };
          };
          
          const { progress: backendProgress, message: progressMessage } = parseProgress(statusData.error_message || '');
          // console.log(`🎯 [VideoDetails] Auto-analysis parsed progress: ${backendProgress}% - ${progressMessage}`);
          
          // Always update synthetic progress with backend progress
          updateSyntheticProgress(videoId, backendProgress, progressMessage);
          
          // Update the local video state with new status
          setVideo(prevVideo => {
            if (!prevVideo) return prevVideo;
            
            // Use the backend progress directly for display
            const displayProgress = Math.max(backendProgress, 5);
            
            const updatedVideo = {
              ...prevVideo,
              status: statusData.status as any,
              error_message: `${displayProgress}% - ${progressMessage}`,
              highlights_count: statusData.highlights_count || prevVideo.highlights?.length || 0,
              clips_count: statusData.clips_count || prevVideo.clips?.length || 0
            };
            
            // console.log(`🔄 [VideoDetails] Auto-analysis updated video state:`, updatedVideo);
            return updatedVideo;
          });
          
          // Update upload task progress
          updateTaskFromVideoStatus(videoId, statusData);
          
          // If analysis is complete, reload the full video details to get clips
          if (statusData.status === 'analyzed') {
            // console.log('✅ [VideoDetails] Auto-analysis completed, loading clips...');
            setAutoAnalyzing(false);
            
            // Only reload once per analysis completion
            if (!analysisCompletedRef.current) {
              analysisCompletedRef.current = true;
              // console.log('🔄 [VideoDetails] Auto-analysis first completion detected, reloading video and clips');
              
              // Small delay to ensure backend has finished all operations
              setTimeout(async () => {
                if (videoId) {
                  try {
                    // console.log('📥 [VideoDetails] Auto-analysis loading video and clips...');
                    // Reload video data and clips
                    const [videoData, clipsData] = await Promise.all([
                      videoApi.getVideo(videoId),
                      optimizedApi.getVideoClips(videoId)
                    ]);
                    setVideo(videoData);
                    setClips(clipsData);
                    // console.log('✅ [VideoDetails] Auto-analysis reloaded:', {
                    //   videoStatus: videoData.status,
                    //   clipCount: clipsData.length
                    // });
                    
                    // Preload thumbnails for clips
                    preloadClipThumbnails(clipsData);
                    
                    // CRITICAL: Invalidate frontend cache when clips are generated
                    // This ensures the dashboard shows the new clips when navigating back
                    if (clipsData.length > 0) {
                      optimizedApi.cache.invalidateVideos(); // Invalidates videos, analytics, and stats
                      optimizedApi.cache.invalidate('clips'); // Invalidate clips cache
                      // console.log('🗑️ [VideoDetails] Invalidated frontend cache after clip generation');
                    }
                    
                    // Show success toast
                    if (!completionToastShownRef.current && clipsData.length > 0) {
                      completionToastShownRef.current = true;
                      toast.success(`🎉 Analysis complete! Generated ${clipsData.length} clips.`);
                    }
                  } catch (err) {
                    console.error('❌ [VideoDetails] Error reloading video after auto-analysis:', err);
                    toast.error('Failed to load generated clips. Please refresh the page.');
                  }
                }
              }, 1000); // 1 second delay
            }
          } else if (statusData.status === 'error') {
            // console.log('❌ [VideoDetails] Auto-analysis failed');
            setAutoAnalyzing(false);
            toast.error('Clip generation failed. Please try again.');
          }
        };

        addVideoStatusPolling(videoId, handleStatusUpdate, 'high');
        
        toast.info('Starting automatic clip generation... Track progress in the upload widget.');
        
      } catch (error: any) {
        logger.error('Error starting auto analysis:', error);
        setAutoAnalyzing(false);
        hasAddedProcessingTaskRef.current = false; // Reset on error
        
        // Check if error is because video is already processing
        if ((error.response?.status === 400 || error.response?.status === 409) && error.response?.data?.detail?.includes('already being processed')) {
          // console.log('Video is already being processed, updating local status');
          setVideo(prevVideo => {
            if (!prevVideo) return prevVideo;
            return { ...prevVideo, status: 'processing' };
          });
          toast.info('Video analysis is already in progress.');
        } else {
          toast.error('Failed to start clip generation. Please try again.');
        }
      }
    };

    // Only start auto-analysis for uploaded videos that don't have clips and aren't already processing
    if (video && video.status === 'uploaded' && clips.length === 0 && !autoAnalyzing) {
      // Automatically start analysis for uploaded videos that don't have clips
      autoStartAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video?.status, clips.length, autoAnalyzing, videoId]); // ✅ Removed function dependencies to prevent re-renders

  // Cleanup polling when component unmounts or video changes
  useEffect(() => {
    return () => {
      // console.log(`🔍 [DEBUG] VideoDetails cleanup triggered for video ${videoId}`);
      isMountedRef.current = false;
      
      if (videoId) {
        // console.log('[VideoDetails] Cleaning up polling for video:', videoId);
        removeVideoStatusPolling(videoId);
        pollingStartedRef.current = false;
      }
      
      // Clean up cached clip thumbnails (blob URLs) to prevent memory leaks
      // Use current clipThumbnails value at cleanup time
      Object.values(clipThumbnails).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]); // Only depend on videoId, not the function reference

  // Global tooltip cleanup - ensure no stuck tooltips
  useEffect(() => {
    const cleanupTooltips = () => {
      document.querySelectorAll('.timeline-tooltip').forEach(tooltip => tooltip.remove());
    };

    // Clean up tooltips when navigating away or on page hide
    const handleBeforeUnload = cleanupTooltips;
    const handleVisibilityChange = () => {
      if (document.hidden) {
        cleanupTooltips();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cleanupTooltips();
    };
  }, []);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setError(null);
    setLoading(true);
    
    // Clear cache to force fresh load
    if (videoId) {
      optimizedApi.cache.invalidate(`video_${videoId}`);
      optimizedApi.cache.invalidate(`video_clips_${videoId}`);
      optimizedApi.cache.invalidate(`clips_${videoId}`);
    }
    
    loadVideoDetails();
  };

  const manualStartAnalysis = async () => {
    if (!video || !videoId) return;
    
    try {
      setAutoAnalyzing(true);
      
      // Initialize synthetic progress for manual analysis
      updateSyntheticProgress(videoId, 0, 'Starting video analysis...');
      
      // IMPROVED: Optimized analysis settings for manual retry with randomization
      const analysisRequest = {
        video_id: videoId,
        sensitivity: 0.5,  // Increased from 0.3 - better detection
        min_clip_duration: 12,  // Increased from 10 - better clip length
        max_clip_duration: 75,  // Reduced from 90 - more digestible clips
        padding_before: 3,
        padding_after: 3,
        enable_audio_analysis: true,
        enable_face_detection: true,
        enable_emotion_detection: true,
        randomize_results: true,  // Always randomize for regeneration
        variation_seed: Math.floor(Math.random() * 1000000)
      };
      
      await videoApi.analyzeVideo(analysisRequest); // Don't use silent for manual analysis
      
      // Update video status to processing
      setVideo(prevVideo => {
        if (!prevVideo) return prevVideo;
        return { ...prevVideo, status: 'processing', error_message: '0% - Starting video analysis...' };
      });
      
      // Start polling for status updates
      const handleStatusUpdate = async (statusData: any) => {
        // Parse progress from error_message to get backend progress
        const parseProgress = (errorMessage: string): { progress: number; message: string } => {
          if (!errorMessage) {
            return { progress: 5, message: 'Processing...' };
          }
          
          const progressMatch = errorMessage.match(/^(\d+(?:\.\d+)?)%\s*[-–—]\s*(.+)$/);
          if (progressMatch) {
            return { progress: parseFloat(progressMatch[1]), message: progressMatch[2].trim() };
          }
          
          const progressMatch2 = errorMessage.match(/^(\d+(?:\.\d+)?)%\s+(.+)$/);
          if (progressMatch2) {
            return { progress: parseFloat(progressMatch2[1]), message: progressMatch2[2].trim() };
          }
          
          const anyProgressMatch = errorMessage.match(/(\d+(?:\.\d+)?)%/);
          if (anyProgressMatch) {
            return { progress: parseFloat(anyProgressMatch[1]), message: errorMessage };
          }
          
          // Special handling for common status messages without percentages
          const lowerMessage = errorMessage.toLowerCase();
          if (lowerMessage.includes('complete') || lowerMessage.includes('finished') || lowerMessage.includes('done')) {
            return { progress: 100, message: errorMessage };
          }
          if (lowerMessage.includes('starting') || lowerMessage.includes('initializing')) {
            return { progress: 5, message: errorMessage };
          }
          if (lowerMessage.includes('analyzing') || lowerMessage.includes('processing')) {
            return { progress: 25, message: errorMessage };
          }
          if (lowerMessage.includes('generating') || lowerMessage.includes('creating')) {
            return { progress: 75, message: errorMessage };
          }
          
          return { progress: 5, message: errorMessage || 'Processing...' };
        };
        
        const { progress: backendProgress, message: progressMessage } = parseProgress(statusData.error_message || '');
        
        // Only update synthetic progress if we have a valid backend progress
        if (backendProgress > 0) {
          updateSyntheticProgress(videoId, backendProgress, progressMessage);
        }
        
        // Update the local video state with new status
        setVideo(prevVideo => {
          if (!prevVideo) return prevVideo;
          
          // Get the current synthetic progress for display
          const currentSyntheticProgress = getSyntheticProgress(videoId);
          const displayProgress = Math.max(backendProgress, currentSyntheticProgress, 5);
          
          return {
            ...prevVideo,
            status: statusData.status as any,
            error_message: `${displayProgress}% - ${progressMessage}`,
            highlights_count: statusData.highlights_count || prevVideo.highlights?.length || 0,
            clips_count: statusData.clips_count || prevVideo.clips?.length || 0
          };
        });
        
        // Update upload task progress
        updateTaskFromVideoStatus(videoId, statusData);
        
        // If analysis is complete, reload clips immediately
        if (statusData.status === 'analyzed') {
          setAutoAnalyzing(false);
          clearSyntheticProgress(videoId); // Clear synthetic progress
          
          // Immediately update video status to show completion
          setVideo(prevVideo => {
            if (!prevVideo) return prevVideo;
            return {
              ...prevVideo,
              status: 'analyzed',
              error_message: '100% - Analysis complete'
            };
          });
          
          // Load clips immediately
          try {
            const clipsData = await optimizedApi.getVideoClips(videoId);
            setClips(clipsData);
            // console.log(`✅ [VideoDetails] Auto-loaded ${clipsData.length} clips after manual analysis`);
            toast.success(`🎉 Analysis complete! Generated ${clipsData.length} clips.`);
            
            // Preload thumbnails for clips
            preloadClipThumbnails(clipsData);
          } catch (clipError) {
            console.error('Error auto-loading clips after manual analysis:', clipError);
            toast.error('Analysis complete but failed to load clips. Please refresh the page.');
          }
          
          removeVideoStatusPolling(videoId);
        } else if (statusData.status === 'error') {
          setAutoAnalyzing(false);
          clearSyntheticProgress(videoId); // Clear synthetic progress on error
          removeVideoStatusPolling(videoId);
          toast.error('Analysis failed. Please try again.');
        }
      };
      
      addVideoStatusPolling(videoId, handleStatusUpdate, 'high');
      
    } catch (error: any) {
      setAutoAnalyzing(false);
      clearSyntheticProgress(videoId); // Clear synthetic progress on error
      console.error('Error starting analysis:', error);
      
      if (error?.response?.status === 409 || (error?.response?.status === 400 && error?.response?.data?.detail?.includes('already being processed'))) {
        toast.error('Video is already being processed. Please wait for completion.');
      } else {
        toast.error('Failed to start analysis. Please try again.');
      }
    }
  };

  const manualStartAnalysisWithRandomization = async () => {
    if (!video || !videoId) return;

    try {
      setAutoAnalyzing(true);

      // Initialize synthetic progress for manual analysis
      updateSyntheticProgress(videoId, 0, 'Starting randomized video analysis...');

      // IMPROVED: Optimized analysis settings for manual retry with randomization
      const analysisRequest = {
        video_id: videoId,
        sensitivity: 0.5,  // Increased from 0.3 - better detection
        min_clip_duration: 12,  // Increased from 10 - better clip length
        max_clip_duration: 75,  // Reduced from 90 - more digestible clips
        padding_before: 3,
        padding_after: 3,
        enable_audio_analysis: true,
        enable_face_detection: true,
        enable_emotion_detection: true,
        randomize_results: true,  // Enable randomization for different clips
        variation_seed: Math.floor(Math.random() * 1000000)  // Random seed for reproducible randomization
      };

      await videoApi.analyzeVideo(analysisRequest); // Don't use silent for manual analysis

      // Update video status to processing
      setVideo(prevVideo => {
        if (!prevVideo) return prevVideo;
        return { ...prevVideo, status: 'processing', error_message: '0% - Starting randomized video analysis...' };
      });

      // Start polling for status updates
      const handleStatusUpdate = async (statusData: any) => {
        // Parse progress from error_message to get backend progress
        const parseProgress = (errorMessage: string): { progress: number; message: string } => {
          if (!errorMessage) {
            return { progress: 5, message: 'Processing...' };
          }

          const progressMatch = errorMessage.match(/^(\d+(?:\.\d+)?)%\s*[-–—]\s*(.+)$/);
          if (progressMatch) {
            return { progress: parseFloat(progressMatch[1]), message: progressMatch[2].trim() };
          }

          const progressMatch2 = errorMessage.match(/^(\d+(?:\.\d+)?)%\s+(.+)$/);
          if (progressMatch2) {
            return { progress: parseFloat(progressMatch2[1]), message: progressMatch2[2].trim() };
          }

          const anyProgressMatch = errorMessage.match(/(\d+(?:\.\d+)?)%/);
          if (anyProgressMatch) {
            return { progress: parseFloat(anyProgressMatch[1]), message: errorMessage };
          }

          // Special handling for common status messages without percentages
          const lowerMessage = errorMessage.toLowerCase();
          if (lowerMessage.includes('complete') || lowerMessage.includes('finished') || lowerMessage.includes('done')) {
            return { progress: 100, message: errorMessage };
          }
          if (lowerMessage.includes('starting') || lowerMessage.includes('initializing')) {
            return { progress: 5, message: errorMessage };
          }
          if (lowerMessage.includes('analyzing') || lowerMessage.includes('processing')) {
            return { progress: 25, message: errorMessage };
          }
          if (lowerMessage.includes('generating') || lowerMessage.includes('creating')) {
            return { progress: 75, message: errorMessage };
          }

          return { progress: 5, message: errorMessage || 'Processing...' };
        };

        const { progress: backendProgress, message: progressMessage } = parseProgress(statusData.error_message || '');

        // Only update synthetic progress if we have a valid backend progress
        if (backendProgress > 0) {
          updateSyntheticProgress(videoId, backendProgress, progressMessage);
        }

        // Update the local video state with new status
        setVideo(prevVideo => {
          if (!prevVideo) return prevVideo;

          // Get the current synthetic progress for display
          const currentSyntheticProgress = getSyntheticProgress(videoId);
          const displayProgress = Math.max(backendProgress, currentSyntheticProgress, 5);

          return {
            ...prevVideo,
            status: statusData.status as any,
            error_message: `${displayProgress}% - ${progressMessage}`,
            highlights_count: statusData.highlights_count || prevVideo.highlights?.length || 0,
            clips_count: statusData.clips_count || prevVideo.clips?.length || 0
          };
        });

        // Update upload task progress
        updateTaskFromVideoStatus(videoId, statusData);

        // If analysis is complete, reload clips immediately
        if (statusData.status === 'analyzed') {
          setAutoAnalyzing(false);
          clearSyntheticProgress(videoId); // Clear synthetic progress

          // Immediately update video status to show completion
          setVideo(prevVideo => {
            if (!prevVideo) return prevVideo;
            return {
              ...prevVideo,
              status: 'analyzed',
              error_message: '100% - Randomized analysis complete'
            };
          });

          // Load clips immediately
          try {
            const clipsData = await optimizedApi.getVideoClips(videoId);
            setClips(clipsData);
            // console.log(`✅ [VideoDetails] Auto-loaded ${clipsData.length} clips after randomized analysis`);
            toast.success(`🎲 Randomized analysis complete! Generated ${clipsData.length} different clips.`);

            // Preload thumbnails for clips
            preloadClipThumbnails(clipsData);
          } catch (clipError) {
            console.error('Error auto-loading clips after randomized analysis:', clipError);
            toast.error('Randomized analysis complete but failed to load clips. Please refresh the page.');
          }

          removeVideoStatusPolling(videoId);
        } else if (statusData.status === 'error') {
          setAutoAnalyzing(false);
          clearSyntheticProgress(videoId); // Clear synthetic progress on error
          removeVideoStatusPolling(videoId);
          toast.error('Randomized analysis failed. Please try again.');
        }
      };

      addVideoStatusPolling(videoId, handleStatusUpdate, 'high');

    } catch (error: any) {
      setAutoAnalyzing(false);
      clearSyntheticProgress(videoId); // Clear synthetic progress on error
      console.error('Error starting randomized analysis:', error);

      if (error?.response?.status === 409 || (error?.response?.status === 400 && error?.response?.data?.detail?.includes('already being processed'))) {
        toast.error('Video is already being processed. Please wait for completion.');
      } else {
        toast.error('Failed to start randomized analysis. Please try again.');
      }
    }
  };

  const deleteClip = async (clipId: string) => {
    const clipToDelete = clips.find(c => c.id === clipId);
    const clipTitle = clipToDelete?.title || 'this clip';
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Clip',
      message: `Are you sure you want to delete "${clipTitle}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await clipApi.deleteClip(clipId);
          setClips(clips.filter(c => c.id !== clipId));
          
          // Invalidate frontend cache when clips are deleted
          optimizedApi.cache.invalidateVideos(); // Invalidates videos, analytics, and stats
          optimizedApi.cache.invalidate('clips'); // Invalidate clips cache
          
          toast.success('Clip deleted successfully');
        } catch (error) {
          logger.error('Error deleting clip:', error);
          toast.error('Failed to delete clip');
        }
      }
    });
  };

  const handleDeleteVideo = () => {
    if (!videoId || !video) return;
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Video',
      message: `Are you sure you want to delete "${getDisplayTitle(video.filename)}" and all associated clips? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await videoApi.deleteVideo(videoId);
          toast.success('Video and clips deleted successfully');
          // Navigate to dashboard with a refresh flag to force data reload
          navigate('/', { replace: true, state: { refresh: true } });
        } catch (error: any) {
          logger.error('Error deleting video:', error);
          
          // Handle 404 errors gracefully - video may have already been deleted
          if (error?.response?.status === 404) {
            toast.success('Video removed successfully');
            // Navigate to dashboard with a refresh flag to force data reload
            navigate('/', { replace: true, state: { refresh: true } });
          } else {
            toast.error('Failed to delete video');
          }
        }
      }
    });
  };

  // Manual retry function for clips
  const retryLoadClips = useCallback(async () => {
    if (!videoId) return;
    
    // Reset error states
    setClipLoadError(null);
    setClipRetryCount(0);
    setLastClipLoadAttempt(0);
    setRateLimitCountdown(0);
    
    // Clear cache to force fresh load
    optimizedApi.cache.invalidate(`video_clips_${videoId}`);
    optimizedApi.cache.invalidate(`clips_${videoId}`);
    
    // Start loading clips
    setClipsLoading(true);
    try {
      const clipsData = await optimizedApi.getVideoClips(videoId);
      setClips(clipsData);
      setClipLoadError(null);
      setClipRetryCount(0);
      
      // Preload thumbnails for clips (inline to avoid dependency issues)
      const clipsWithVideoSource = clipsData.filter(clip => clip.metadata?.source_video_url);
      if (clipsWithVideoSource.length > 0) {
        // Use the thumbnail cache to preload
        const thumbnailCacheModule = await import('../lib/thumbnailCache');
        const thumbnailCache = thumbnailCacheModule.default;
        clipsWithVideoSource.forEach(async (clip) => {
          try {
            if (clip.metadata?.source_video_url) {
              const thumbnailTime = clip.start_time + (clip.duration * 0.3);
              await thumbnailCache.preloadThumbnail(
                clip.id, 
                clip.metadata.source_video_url, 
                thumbnailTime
              );
            }
          } catch (error) {
            console.error(`Failed to preload thumbnail for clip ${clip.id}:`, error);
          }
        });
      }
      
      toast.success(`Loaded ${clipsData.length} clips successfully!`);
    } catch (clipError: any) {
      logger.error('Error loading clips:', clipError);
      const errorMessage = clipError?.response?.data?.detail || clipError?.message || 'Failed to load clips';
      setClipLoadError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setClipsLoading(false);
    }
  }, [videoId]);

  // Countdown timer for rate limiting
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    
    if (rateLimitCountdown > 0) {
      timer = setInterval(() => {
        setRateLimitCountdown(prev => {
          if (prev <= 1) {
            setClipLoadError(null); // Clear error when countdown reaches 0
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateLimitCountdown > 0]); // ✅ Only depend on whether countdown is active, not the value

  // Merge streamed clips with existing clips
  useEffect(() => {
    if (streamedClips.length > 0) {
      // console.log(`📥 [VideoDetails] Received ${streamedClips.length} streamed clips, current clips: ${clips.length}`);
      
      setClips(prevClips => {
        // Create a map of existing clips by ID for efficient lookup
        const existingClipsMap = new Map(prevClips.map(clip => [clip.id, clip]));
        
        // Add new streamed clips that don't already exist
        const newClips = [...prevClips];
        let newClipCount = 0;
        
        streamedClips.forEach(streamedClip => {
          if (!existingClipsMap.has(streamedClip.id)) {
            newClips.push(streamedClip);
            newClipCount++;
            // console.log(`📥 [VideoDetails] Added new clip: ${streamedClip.title} (${streamedClip.id})`);
          }
        });
        
        if (newClipCount > 0) {
          // Sort by start_time and show toast only once for new clips
          const sortedClips = newClips.sort((a, b) => a.start_time - b.start_time);
          // console.log(`✅ [VideoDetails] Merged ${newClipCount} new clips, total: ${sortedClips.length}`);
          
          // Invalidate frontend cache when new clips are streamed
          optimizedApi.cache.invalidateVideos(); // Invalidates videos, analytics, and stats
          optimizedApi.cache.invalidate('clips'); // Invalidate clips cache
          
          // Show notification only for batch updates, not individual clips to avoid spam
          // Individual clips are shown via WebSocket as they stream in
          if (newClipCount > 1) {
            toast.success(`${newClipCount} new clips generated!`);
          }
          // Single clips don't need toast - the streaming UI shows them appearing
          return sortedClips;
        }
        
        return prevClips;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamedClips.length]); // ✅ Only depend on the length, not the array itself

  // Function to preload clip thumbnails with caching
  const preloadClipThumbnails = useCallback(async (clipsList: Clip[]) => {
    const clipsWithVideoSource = clipsList.filter(clip => clip.metadata?.source_video_url);
    
    if (clipsWithVideoSource.length === 0) return;

    // Check cache first and load cached thumbnails immediately
    const cachedThumbnails: Record<string, string> = {};
    const clipsNeedingThumbnails: Clip[] = [];

    clipsWithVideoSource.forEach(clip => {
      const cachedUrl = thumbnailCache.get(clip.id);
      if (cachedUrl) {
        cachedThumbnails[clip.id] = cachedUrl;
      } else {
        clipsNeedingThumbnails.push(clip);
      }
    });

    // Immediately set cached thumbnails
    if (Object.keys(cachedThumbnails).length > 0) {
      setClipThumbnails(prev => ({ ...prev, ...cachedThumbnails }));
    }

    // Load thumbnails for clips not in cache
    if (clipsNeedingThumbnails.length === 0) {
      return; // All thumbnails were cached
    }

    // Generate thumbnails for clips that need them
    const thumbnailPromises = clipsNeedingThumbnails.map(async (clip) => {
      try {
        if (clip.metadata?.source_video_url) {
          // Use the clip start time + 30% of duration for a good thumbnail
          const thumbnailTime = clip.start_time + (clip.duration * 0.3);
          const thumbnailUrl = await thumbnailCache.preloadThumbnail(
            clip.id, 
            clip.metadata.source_video_url, 
            thumbnailTime
          );
          return { clipId: clip.id, thumbnailUrl };
        }
        return { clipId: clip.id, thumbnailUrl: null };
      } catch (error) {
        console.error(`Failed to generate thumbnail for clip ${clip.id}:`, error);
        return { clipId: clip.id, thumbnailUrl: null };
      }
    });

    try {
      const thumbnailResults = await Promise.allSettled(thumbnailPromises);
      const newThumbnails: Record<string, string> = {};
      
      thumbnailResults.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.thumbnailUrl) {
          newThumbnails[result.value.clipId] = result.value.thumbnailUrl;
        }
      });
      
      if (Object.keys(newThumbnails).length > 0) {
        setClipThumbnails(prev => ({ ...prev, ...newThumbnails }));
      }
    } catch (error) {
      console.error('Error preloading clip thumbnails:', error);
    }
  }, []);

  const handleClipNavigation = (clip: Clip, event?: React.MouseEvent) => {
    // Block navigation for clips that haven't finished processing yet
    const isClipReady = clip.metadata?.file_verified !== false;
    
    if (!isClipReady) {
      event?.preventDefault();
      toast.info('Clip is still being processed, please wait a moment...');
      return;
    }
    
    // Prefetch clip details for smoother navigation
    optimizedApi.prefetch.clipDetails(clip.id);
    
    // Only push navigation for direct clicks; Links will handle navigation themselves
    if (!event) {
      navigate(`/clips/${clip.id}`);
    }
  };

  // For old full-screen loading (only when auth is still loading and we have nothing)
  if (authLoading && !video) {
    return (
      <StartupLoadingScreen 
        isVisible={true}
        customTitle="Loading Video"
        customMessages={[
          "Fetching video details...",
          "Loading metadata...",
          "Preparing clips...",
          "Almost ready..."
        ]}
      />
    );
  }

  // Handle different error states
  if (error && !video) {
    return (
      <div className="relative overflow-x-hidden" style={{ minHeight: 'calc(100vh - 64px)' }}>
        {/* Subtle animated background */}
        <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
        
        <div className="flex items-center justify-center relative z-10" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center max-w-md mx-auto px-6">
            <div className="mb-6">
              {error === 'Request timed out while loading video details. This usually happens with large videos that are still processing.' && (
                <Clock className="h-16 w-16 text-red-500 mx-auto mb-4" />
              )}
              {error === 'Video not found. It may have been deleted or the link is incorrect.' && (
                <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              )}
              {error === 'Network connection problem. Please check your internet connection.' && (
                <RefreshCw className="h-16 w-16 text-gray-500 mx-auto mb-4" />
              )}
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {error === 'Request timed out while loading video details. This usually happens with large videos that are still processing.' && 'Request Timed Out'}
              {error === 'Video not found. It may have been deleted or the link is incorrect.' && 'Video Not Found'}
              {error === 'Network connection problem. Please check your internet connection.' && 'Connection Problem'}
            </h2>
            
            <p className="text-gray-600 mb-6">{error}</p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {error !== 'Video not found. It may have been deleted or the link is incorrect.' && (
                <button
                  onClick={handleRetry}
                  className="btn-primary flex items-center justify-center"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {retryCount > 0 ? `Retry (${retryCount + 1})` : 'Retry'}
                </button>
              )}
              
              <Link to="/" className="btn-secondary flex items-center justify-center">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
            </div>
            
            {error === 'Request timed out while loading video details. This usually happens with large videos that are still processing.' && (
              <div className="mt-6 p-4 bg-red-50 rounded-lg text-sm text-red-800">
                <p className="font-medium mb-2">💡 Why did this happen?</p>
                <ul className="text-left space-y-1 list-disc list-inside">
                  <li>Large videos can take 5-15 minutes to process</li>
                  <li>Your video might still be analyzing in the background</li>
                  <li>Network timeouts can occur with slow connections</li>
                </ul>
                <p className="mt-2 font-medium">
                  Try refreshing in a few minutes, or check your dashboard for processing status.
                </p>
              </div>
            )}
            
            {error === 'Network connection problem. Please check your internet connection.' && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-800">
                <p className="font-medium mb-2">🔧 Troubleshooting:</p>
                <ul className="text-left space-y-1 list-disc list-inside">
                  <li>Check your internet connection</li>
                  <li>Try refreshing the page</li>
                  <li>Wait a moment and try again</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Video not found</p>
        <Link to="/" className="btn-primary mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="relative overflow-x-hidden" style={{ minHeight: 'calc(100vh - 64px)' }}>
      {/* Sticky Conversion Banner for Guests */}
      {(!currentUser || isGuest) && clips.length > 0 && video?.status === 'analyzed' && (
        <div className="sticky top-0 z-50 bg-gradient-to-r from-red-500 to-red-600 shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">
                    🎉 Awesome! Your {clips.length} clip{clips.length !== 1 ? 's are' : ' is'} ready!
                  </p>
                  <p className="text-red-100 text-xs">
                    Create a free account to upload unlimited videos & keep clips forever
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Link
                  to="/signup"
                  className="bg-white text-red-600 px-4 py-1.5 rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors"
                >
                  Sign Up Free
                </Link>
                <Link
                  to="/login"
                  className="text-white border border-white border-opacity-40 px-4 py-1.5 rounded-lg font-medium text-sm hover:bg-white hover:bg-opacity-10 transition-colors"
                >
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Subtle animated background */}
      <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
      
      <div className="space-y-8 relative z-10 px-6 sm:px-8 py-8">
        {(!currentUser || isGuest) && (
          <div className="mb-4 p-4 bg-white border-2 border-red-500 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">!</span>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-1">Guest Mode - Limited Features</h3>
                <p className="text-gray-700 text-sm mb-2">
                  Your clips will be <strong className="text-red-600">deleted after this session</strong>. Create a free account to unlock:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                  <div className="flex items-center">
                    <CheckCircle className="h-3 w-3 text-red-500 mr-1.5" />
                    Unlimited video uploads
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-3 w-3 text-red-500 mr-1.5" />
                    Permanent clip storage
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-3 w-3 text-red-500 mr-1.5" />
                    All export formats (MP4, WebM, GIF)
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-3 w-3 text-red-500 mr-1.5" />
                    Advanced AI features
                  </div>
                </div>
                <Link
                  to="/signup"
                  className="inline-flex items-center text-red-600 font-semibold text-sm hover:text-red-700 transition-colors"
                >
                  Create Free Account →
                </Link>
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/" className="btn-secondary">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
            
            <div>
              <div className="flex items-center space-x-3">
                {video && video.metadata && !video.filename.startsWith('import_pending_') && video.metadata.duration > 0 ? (
                  <>
                    <PlatformIcon 
                      platform={video.metadata?.source_platform} 
                      size={24}
                    />
                    <h1 className="text-3xl font-bold text-gray-900">
                      {getDisplayTitle(video.filename)}
                    </h1>
                  </>
                ) : (
                  <>
                    {/* Skeleton loader for icon */}
                    <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
                    {/* Skeleton loader for title */}
                    <div className="h-9 bg-gray-200 rounded animate-pulse w-96"></div>
                  </>
                )}
              </div>
              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                {video && video.metadata && !video.filename.startsWith('import_pending_') && video.metadata.duration > 0 ? (
                  <>
                    <span>Duration: {formatDuration(video.metadata.duration)}</span>
                    {isValidFileSize(video.metadata.file_size) && (
                      <span>Size: {formatFileSize(video.metadata.file_size)}</span>
                    )}
                    <span>Resolution: {video.metadata.resolution || 'Unknown'}</span>
                    <span>Codec: {video.metadata.codec || 'Unknown'}</span>
                  </>
                ) : (
                  <>
                    {/* Skeleton loaders for metadata */}
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-20"></div>
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-28"></div>
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <Tooltip
            content={(!currentUser || isGuest) ? 'Sign in to use' : 'Delete video and all clips'}
            position="top"
          >
            <button
              onClick={handleDeleteVideo}
              className="btn-danger flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!currentUser || isGuest}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Video
            </button>
          </Tooltip>
        </div>

        {/* Video Info Card */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Video Information
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Video Thumbnail */}
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
              {!video || video.filename.startsWith('import_pending_') || !video.thumbnail_url ? (
                // Skeleton loader for thumbnail while loading/importing
                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse flex flex-col items-center justify-center">
                  <Loader2 size={48} className="text-gray-400 animate-spin" />
                </div>
              ) : video?.thumbnail_url ? (
                <img 
                  src={video.thumbnail_url}
                  alt={video.filename}
                  className="w-full h-full object-cover"
                  onError={() => setVideoThumbError(true)}
                />
              ) : videoThumbUrl && !videoThumbError ? (
                <img
                  src={videoThumbUrl}
                  alt={video?.filename || 'Video thumbnail'}
                  className="w-full h-full object-cover"
                  onError={() => setVideoThumbError(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <Play size={48} />
                  <span className="ml-2">No thumbnail available</span>
                </div>
              )}
            </div>
            
            {/* Video Metadata */}
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-500">Filename</label>
                {video && !video.filename.startsWith('import_pending_') && video.metadata.duration > 0 ? (
                  <p className="text-gray-900 font-medium">{video.filename}</p>
                ) : (
                  <div className="h-6 bg-gray-200 rounded animate-pulse w-full"></div>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-500">Duration</label>
                {video && !video.filename.startsWith('import_pending_') && video.metadata.duration > 0 ? (
                  <p className="text-gray-900">{formatDuration(video.metadata.duration)}</p>
                ) : (
                  <div className="h-6 bg-gray-200 rounded animate-pulse w-24"></div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-500">Resolution</label>
                  {video && !video.filename.startsWith('import_pending_') && video.metadata.duration > 0 ? (
                    <p className="text-gray-900">{video.metadata.resolution || 'Unknown'}</p>
                  ) : (
                    <div className="h-6 bg-gray-200 rounded animate-pulse w-28"></div>
                  )}
                </div>
                <div>
                  <label className="text-sm text-gray-500">Codec</label>
                  {video && !video.filename.startsWith('import_pending_') && video.metadata.duration > 0 ? (
                    <p className="text-gray-900">{video.metadata.codec || 'Unknown'}</p>
                  ) : (
                    <div className="h-6 bg-gray-200 rounded animate-pulse w-20"></div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Visualization */}
        {showTimeline && video.highlights.length > 0 && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Video Timeline</h2>
              <button
                onClick={() => setShowTimeline(!showTimeline)}
                className="btn-secondary text-sm"
              >
                Hide Timeline
              </button>
            </div>
            
            <div className="timeline-container">
              <div className="timeline-bar">
                {video.highlights.map((highlight) => {
                  const leftPercent = (highlight.start_time / video.metadata.duration) * 100;
                  const widthPercent = ((highlight.end_time - highlight.start_time) / video.metadata.duration) * 100;
                  
                  return (
                    <div
                      key={highlight.id}
                      className={`timeline-highlight ${highlight.type.replace('_', '-')}`}
                      style={{
                        left: `${leftPercent}%`,
                        width: `${widthPercent}%`
                      }}
                      onMouseEnter={(e) => {
                        const tooltip = document.createElement('div');
                        tooltip.className = 'timeline-tooltip';
                        tooltip.textContent = `${highlight.type}: ${highlight.description}\n${formatDuration(highlight.start_time)} - ${formatDuration(highlight.end_time)}`;
                        
                        const rect = e.currentTarget.getBoundingClientRect();
                        tooltip.style.left = `${rect.left + rect.width / 2}px`;
                        tooltip.style.top = `${rect.top - 8}px`;
                        tooltip.style.transform = 'translateX(-50%) translateY(-100%)';
                        
                        document.body.appendChild(tooltip);
                        requestAnimationFrame(() => {
                          tooltip.classList.add('visible');
                        });
                        
                        e.currentTarget.setAttribute('data-tooltip-id', tooltip.id = `tooltip-${Date.now()}`);
                      }}
                      onMouseLeave={(e) => {
                        const tooltipId = e.currentTarget.getAttribute('data-tooltip-id');
                        if (tooltipId) {
                          const tooltip = document.getElementById(tooltipId);
                          if (tooltip) {
                            tooltip.classList.remove('visible');
                            setTimeout(() => tooltip.remove(), 200);
                          }
                        }
                      }}
                    />
                  );
                })}
              </div>
              
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>0:00</span>
                <span>{formatDuration(video.metadata.duration)}</span>
              </div>
              
              {/* Legend */}
              <div className="flex items-center space-x-4 mt-4 text-sm">
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-red-500 rounded"></div>
                  <span>Audio Spike</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-gray-600 rounded"></div>
                  <span>Facial Emotion</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-gray-900 rounded"></div>
                  <span>Combined</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Processing Status with Smart Progress */}
        {video.status === 'processing' && (
          <SmartProgressIndicator
            progress={(() => {
              const progressMatch = video.error_message?.match(/(\d+(?:\.\d+)?)%/);
              return progressMatch ? parseFloat(progressMatch[1]) : 0;
            })()}
            status={video.error_message || 'Analyzing video content...'}
            videoSize={video.metadata?.file_size}
            videoDuration={video.metadata?.duration}
            stage="analysis"
          />
        )}
        
        {/* Analysis Complete Status - Show briefly when just completed */}
        {video.status === 'analyzed' && clips.length === 0 && !clipsLoading && (
          <div className="card bg-gray-50 border-gray-200">
            <div className="flex items-center">
              <CheckCircle className="h-6 w-6 text-gray-600 mr-3" />
              <div className="flex-1">
                <p className="text-gray-900 font-medium">Analysis Complete!</p>
                <p className="text-gray-700 text-sm">
                  Loading your generated clips...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Auto-Analysis Status for uploaded videos */}
        {video.status === 'uploaded' && autoAnalyzing && (
          <div className="card bg-primary-50 border-primary-200">
            <div className="flex items-center">
              <div className="relative mr-3">
                <Sparkles className="h-6 w-6 text-primary-600 animate-pulse" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full animate-ping"></div>
              </div>
              <div className="flex-1">
                <p className="text-primary-800 font-medium">Generating Clips Automatically</p>
                <p className="text-primary-700 text-sm">
                  We're analyzing your video to find the best moments and generate clips automatically.
                  This usually takes 1-5 minutes but can take a while for longer videos.
                </p>
                <div className="mt-3 flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-primary-600 text-xs font-medium">
                    Finding highlights...
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Real-time Clips During Processing */}
        {video.status === 'processing' && (
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Sparkles className="h-5 w-5 text-primary-600 mr-2 animate-pulse" />
                Clips Being Generated ({streamedClips.length + clips.length})
              </h2>
              {clipStreamConnected && (
                <div className="flex items-center text-sm text-red-600">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></div>
                  Live Updates
                </div>
              )}
            </div>
            
            {streamedClips.length === 0 && clips.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Skeleton placeholder cards */}
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <div
                    key={`skeleton-${index}`}
                    className="border-2 rounded-lg overflow-hidden border-gray-200 animate-pulse"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Skeleton thumbnail */}
                    <div className="aspect-video bg-gradient-to-br from-gray-200 to-gray-300 relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                    </div>
                    
                    {/* Skeleton content */}
                    <div className="p-4 space-y-3">
                      {/* Title skeleton */}
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      </div>
                      
                      {/* Duration skeleton */}
                      <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      
                      {/* Tags and number skeleton */}
                      <div className="flex items-center justify-between">
                        <div className="flex space-x-1">
                          <div className="h-6 w-20 bg-gray-200 rounded-md"></div>
                          <div className="h-6 w-16 bg-gray-200 rounded-md"></div>
                        </div>
                        <div className="h-4 w-8 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600 flex items-center">
                    <span className="text-gray-600 mr-2">🎬</span>
                    <span>
                      <strong>Real-time Generation:</strong> Clips are appearing as they're completed. 
                      More clips may be added as processing continues.
                    </span>
                  </p>
                </div>
                
                {/* Real-time Clips Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Display both streamedClips and regular clips */}
                  {[...clips, ...streamedClips.filter(streamedClip => !clips.some(clip => clip.id === streamedClip.id))].map((clip, index) => (
                    <Link
                      key={clip.id}
                      to={`/clips/${clip.id}`}
                      className="border-2 rounded-lg overflow-hidden cursor-pointer transition-all duration-500 border-red-200 hover:border-red-400 hover:shadow-lg animate-fade-in block"
                      style={{ animationDelay: `${index * 100}ms` }}
                      onClick={(e) => handleClipNavigation(clip, e)}
                      onMouseEnter={() => optimizedApi.prefetch.clipDetails(clip.id)}
                    >
                      <div className="aspect-video bg-gray-100 flex items-center justify-center relative group">
                        {clip.thumbnail_url || clip.id ? (
                          <>
                            <img
                              src={clip.thumbnail_url || `/api/v1/clips/${clip.id}/thumbnail`}
                              alt={clip.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // Hide broken image and show fallback
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.parentElement?.querySelector('.thumbnail-fallback') as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                              <Play className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-all duration-200" />
                            </div>
                            {/* Fallback for failed thumbnail loads */}
                            <div className="thumbnail-fallback absolute inset-0 bg-gradient-to-br from-gray-600 to-gray-800 items-center justify-center" style={{ display: 'none' }}>
                              <div className="text-center text-white">
                                <Play className="h-8 w-8 mx-auto mb-2" />
                                <p className="text-xs font-medium">Clip {index + 1}</p>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
                            <div className="text-center text-white">
                              <Play className="h-8 w-8 mx-auto mb-2" />
                              <p className="text-xs font-medium">Clip {index + 1}</p>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-gray-900 mb-1">
                          {clip.title}
                        </h3>
                        <p className="text-xs text-gray-600 mb-2">
                          {formatDuration(clip.duration)} • Just generated
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex space-x-1">
                            {(clip.tags || []).slice(0, 2).map((tag) => (
                              <span key={tag} className={`text-xs font-medium px-2 py-1 rounded-md ${
                                tag === 'audio_spike' ? 'bg-red-100 text-red-700' :
                                tag === 'facial_emotion' || tag === 'scene_change' ? 'bg-gray-100 text-gray-700' :
                                tag === 'speech_highlight' ? 'bg-gray-900 text-white border border-gray-800' :
                                tag === 'combined' ? 'bg-gray-50 text-gray-700 border border-gray-300' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                          
                          <div className="text-xs text-gray-600 font-medium">
                            #{index + 1}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Error Status */}
        {video.status === 'error' && (
          <div className="card bg-red-50 border-red-200">
            <div className="flex items-center">
              <Trash2 className="h-6 w-6 text-red-600 mr-3" />
              <div>
                <p className="text-red-800 font-medium">Analysis Failed</p>
                <p className="text-red-700 text-sm">
                  {video.error_message || 'An error occurred during video analysis.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Generated Clips */}
        {video.status === 'analyzed' && (
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Generated Clips ({clips.length})
              </h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={manualStartAnalysisWithRandomization}
                  disabled={autoAnalyzing}
                  className="btn-secondary flex items-center text-sm"
                  title="Generate different clips with randomized analysis"
                >
                  {autoAnalyzing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Shuffle className="h-4 w-4 mr-2" />
                      Generate Different Clips
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {clipsLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="h-8 w-8 text-primary-600 mx-auto mb-4 animate-spin" />
                <p className="text-gray-600">Loading clips...</p>
              </div>
            ) : clipLoadError ? (
              <div className="text-center py-8">
                {/* Error message and icon hidden as requested - only showing retry button */}
                {!clipLoadError.includes('Rate limited') && rateLimitCountdown === 0 && (
                  <button
                    onClick={retryLoadClips}
                    className="btn-primary flex items-center mx-auto"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry Loading Clips
                  </button>
                )}
              </div>
            ) : clips.length > 0 ? (
              <>
                
                {/* Clips Timeline */}
                <div className="mb-6 p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Sparkles className="h-5 w-5 text-primary-600 mr-2" />
                      Clip Timeline
                    </h3>
                    <span className="text-sm text-gray-600 bg-white px-3 py-1 rounded-lg border">
                      {clips.length} clip{clips.length !== 1 ? 's' : ''} found
                    </span>
                  </div>
                  
                  {/* Legend */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Highlight Types</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 text-sm">
                      <div className="flex items-center space-x-2 bg-white p-2 rounded-lg border">
                        <div className="w-4 h-4 bg-gradient-to-r from-red-500 to-red-600 rounded shadow-sm"></div>
                        <div>
                          <span className="font-medium text-gray-900">Audio Energy</span>
                          <p className="text-xs text-gray-600">High volume/excitement</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 bg-white p-2 rounded-lg border">
                        <div className="w-4 h-4 bg-gradient-to-r from-gray-900 to-black rounded shadow-sm"></div>
                        <div>
                          <span className="font-medium text-gray-900">Speech Moment</span>
                          <p className="text-xs text-gray-600">Key spoken words</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 bg-white p-2 rounded-lg border">
                        <div className="w-4 h-4 bg-gradient-to-r from-red-400 to-red-500 rounded shadow-sm"></div>
                        <div>
                          <span className="font-medium text-gray-900">Emotional Peak</span>
                          <p className="text-xs text-gray-600">Strong sentiment</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 bg-white p-2 rounded-lg border">
                        <div className="w-4 h-4 bg-gradient-to-r from-gray-400 to-gray-500 rounded shadow-sm"></div>
                        <div>
                          <span className="font-medium text-gray-900">Visual Change</span>
                          <p className="text-xs text-gray-600">Faces & scenes</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 bg-white p-2 rounded-lg border">
                        <div className="w-4 h-4 bg-gradient-to-r from-gray-100 via-gray-300 to-gray-500 rounded shadow-sm"></div>
                        <div>
                          <span className="font-medium text-gray-900">Multi-Signal</span>
                          <p className="text-xs text-gray-600">Combined factors</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="relative">
                    {/* Timeline background */}
                    <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden shadow-inner relative">
                      {/* Grid lines for time markers */}
                      <div className="absolute inset-0">
                        {[...Array(11)].map((_, i) => (
                          <div
                            key={i}
                            className="absolute top-0 bottom-0 w-px bg-gray-300 opacity-50"
                            style={{ left: `${i * 10}%` }}
                          />
                        ))}
                      </div>
                      
                      {/* Clip segments */}
                      {clips.map((clip, index) => {
                        const leftPercent = (clip.start_time / video.metadata.duration) * 100;
                        const widthPercent = ((clip.end_time - clip.start_time) / video.metadata.duration) * 100;
                        
                        // Determine color based on clip type/tags (white, black, red, gray theme)
                        let colorClass = 'from-red-500 to-red-600'; // Audio Energy - bright red
                        const tags = clip.tags || [];
                        const friendlyTags = tags.map(formatTagLabel);
                        
                        if (tags.includes('speech_highlight')) {
                          colorClass = 'from-gray-900 to-black'; // Speech - black
                        } else if (tags.includes('sentiment_peak')) {
                          colorClass = 'from-red-400 to-red-500'; // Emotional - lighter red
                        } else if (tags.includes('facial_emotion') || tags.includes('scene_change')) {
                          colorClass = 'from-gray-400 to-gray-500'; // Visual - medium gray
                        } else if (tags.includes('combined')) {
                          colorClass = 'from-gray-100 to-gray-500'; // Multi-Signal - soft gray
                        }
                        
                        return (
                          <div
                            key={clip.id}
                            className={`absolute h-full bg-gradient-to-r ${colorClass} opacity-85 hover:opacity-100 transition-all duration-200 cursor-pointer rounded shadow-sm hover:shadow-md transform hover:scale-y-110`}
                            style={{
                              left: `${leftPercent}%`,
                              width: `${Math.max(widthPercent, 1.5)}%`, // Minimum 1.5% width for visibility
                              zIndex: 10 + index,
                            }}
                            onMouseEnter={(e) => {
                              // Clean up any existing tooltips first
                              document.querySelectorAll('.timeline-tooltip').forEach(tooltip => tooltip.remove());
                              
                              const tooltip = document.createElement('div');
                              tooltip.className = 'timeline-tooltip';
                              const tooltipLines = [
                                clip.title,
                                `${formatDuration(clip.start_time)} - ${formatDuration(clip.end_time)}`
                              ];
                              if (friendlyTags.length) {
                                tooltipLines.push(`Type: ${friendlyTags.join(', ')}`);
                              }
                              tooltip.textContent = tooltipLines.join('\n');
                              
                              const rect = e.currentTarget.getBoundingClientRect();
                              tooltip.style.left = `${rect.left + rect.width / 2}px`;
                              tooltip.style.top = `${rect.top - 8}px`;
                              tooltip.style.transform = 'translateX(-50%) translateY(-100%)';
                              
                              document.body.appendChild(tooltip);
                              requestAnimationFrame(() => {
                                tooltip.classList.add('visible');
                              });
                              
                              e.currentTarget.setAttribute('data-tooltip-id', tooltip.id = `tooltip-${Date.now()}`);
                            }}
                            onMouseLeave={(e) => {
                              const tooltipId = e.currentTarget.getAttribute('data-tooltip-id');
                              if (tooltipId) {
                                const tooltip = document.getElementById(tooltipId);
                                if (tooltip) {
                                  tooltip.classList.remove('visible');
                                  setTimeout(() => tooltip.remove(), 200);
                                }
                              }
                            }}
                            onMouseDown={(e) => {
                              // Clean up tooltip immediately when mouse is pressed down
                              const tooltipId = e.currentTarget.getAttribute('data-tooltip-id');
                              if (tooltipId) {
                                const tooltip = document.getElementById(tooltipId);
                                if (tooltip) {
                                  tooltip.remove();
                                }
                                e.currentTarget.removeAttribute('data-tooltip-id');
                              }
                            }}
                            onFocus={() => {
                              // Clean up any tooltips when element gains focus
                              document.querySelectorAll('.timeline-tooltip').forEach(tooltip => tooltip.remove());
                            }}
                            onClick={(e) => {
                              // Clean up tooltip immediately when clicked
                              const tooltipId = e.currentTarget.getAttribute('data-tooltip-id');
                              if (tooltipId) {
                                const tooltip = document.getElementById(tooltipId);
                                if (tooltip) {
                                  tooltip.remove();
                                }
                                e.currentTarget.removeAttribute('data-tooltip-id');
                              }
                              // Also clean up any other lingering tooltips
                              document.querySelectorAll('.timeline-tooltip').forEach(tooltip => tooltip.remove());
                              
                              handleClipNavigation(clip);
                            }}
                          />
                        );
                      })}
                    </div>
                    
                    {/* Time markers */}
                    <div className="flex justify-between text-xs text-gray-500 mt-3 px-1">
                      <span className="font-medium">0:00</span>
                      {[...Array(4)].map((_, i) => {
                        const timePercent = (i + 1) * 20;
                        const timeSeconds = (video.metadata.duration * timePercent) / 100;
                        return (
                          <span key={i} className="hidden sm:block">
                            {formatDuration(timeSeconds)}
                          </span>
                        );
                      })}
                      <span className="font-medium">{formatDuration(video.metadata.duration)}</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600 flex items-start">
                      <span className="text-primary-600 mr-2">💡</span>
                      <span>
                        <strong>Interactive Timeline:</strong> Each colored segment represents a highlight clip. 
                        Click on any segment or clip card below to view details and export options.
                      </span>
                    </p>
                  </div>
                </div>

                {/* Bulk Export Section */}
                <div className="mb-6 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <FileVideo className="h-5 w-5 text-gray-700 mr-2" />
                    Clips ({clips.length})
                  </h3>
                </div>

                {/* Clips Grid */}
                {/* Show message if no clips available */}
                {clips.filter(clip => clip.file_url || clip.metadata?.source_video_url).length === 0 && clips.length > 0 && (
                  <div className="col-span-full text-center py-8">
                    <div className="text-gray-400 mb-2">
                      <Clock className="h-8 w-8 mx-auto mb-3" />
                    </div>
                    <p className="text-gray-600 font-medium">Clips are processing</p>
                    <p className="text-sm text-gray-500 mt-1">Your clips will appear here once they're ready</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(() => {
                    // Find the single best clip (first one with highest score)
                    const sortedByQuality = [...clips].sort((a, b) => (b.quality_score || 0) - (a.quality_score || 0));
                    const topClipId = sortedByQuality.length > 1 && sortedByQuality[0].quality_score ? sortedByQuality[0].id : null;
                    
                    return clips.map((clip) => {
                      const isClipReady = clip.metadata?.file_verified !== false;
                      // Only the single best clip gets the badge
                      const isTopPick = topClipId === clip.id;
                    
                    return (
                      <Link
                        key={clip.id}
                        to={`/clips/${clip.id}`}
                        className={`border-2 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 block ${
                          isClipReady 
                            ? 'border-gray-200 hover:border-primary-500 hover:shadow-lg' 
                            : 'border-gray-200 bg-gray-50'
                        }`}
                        onClick={(e) => handleClipNavigation(clip, e)}
                        onMouseEnter={() => optimizedApi.prefetch.clipDetails(clip.id)}
                      >
                        <div className="aspect-video bg-gray-900 flex items-center justify-center relative group">
                          {/* Processing overlay for clips that aren't ready */}
                          {!isClipReady && (
                            <div className="absolute inset-0 bg-gray-500 bg-opacity-90 flex items-center justify-center z-20">
                              <div className="text-center text-white">
                                <RefreshCw className="h-6 w-6 mx-auto mb-2 animate-spin" />
                                <p className="text-xs font-medium">Processing...</p>
                              </div>
                            </div>
                          )}
                          
                          {/* Top Pick Badge - subtle star in corner */}
                          {isTopPick && (
                            <div className="absolute top-2 right-2 z-10">
                              <Tooltip content="Top Pick - Highest quality clip">
                                <div className="bg-red-500 rounded-full p-1.5 shadow-lg backdrop-blur-sm bg-opacity-90">
                                  <Star className="h-3.5 w-3.5 text-white fill-white" />
                                </div>
                              </Tooltip>
                            </div>
                          )}
                          
                          {/* Show clip thumbnail if available */}
                          {clip.thumbnail_url || clip.id ? (
                            <>
                              <img
                                src={clip.thumbnail_url || `/api/v1/clips/${clip.id}/thumbnail`}
                                alt={clip.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // Hide broken image and show fallback
                                  e.currentTarget.style.display = 'none';
                                  const fallback = e.currentTarget.parentElement?.querySelector('.video-fallback') as HTMLElement;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                              
                              {/* Play overlay */}
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                                <Play className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-all duration-200" />
                              </div>
                            </>
                          ) : clipThumbnails[clip.id] ? (
                            <>
                              <img
                                src={clipThumbnails[clip.id]}
                                alt={clip.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // Remove failed thumbnail and show fallback
                                  setClipThumbnails(prev => {
                                    const updated = { ...prev };
                                    delete updated[clip.id];
                                    return updated;
                                  });
                                  e.currentTarget.style.display = 'none';
                                  const fallback = e.currentTarget.parentElement?.querySelector('.video-fallback') as HTMLElement;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                              
                              {/* Play overlay */}
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                                <Play className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-all duration-200" />
                              </div>
                            </>
                          ) : (
                            <div className="video-fallback w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                              <div className="text-center text-white">
                                <Play className="h-12 w-12 mx-auto mb-2 opacity-60" />
                                <p className="text-sm font-medium">Clip {clips.indexOf(clip) + 1}</p>
                                <p className="text-xs opacity-60 mt-1">{formatDuration(clip.duration)}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="p-4">
                          <h3 className="text-sm font-medium text-gray-900 mb-1">
                            {clip.title}
                          </h3>
                          <p className="text-xs text-gray-600 mb-2">
                            {formatDuration(clip.duration)} • {formatTimestamp(clip.created_at)}
                          </p>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex space-x-1">
                              {(clip.tags || []).slice(0, 2).map((tag) => (
                              <span key={tag} className={`text-xs font-medium px-2 py-1 rounded-md ${
                                tag === 'audio_spike' ? 'bg-red-100 text-red-700' :
                                tag === 'facial_emotion' || tag === 'scene_change' ? 'bg-gray-100 text-gray-700' :
                                tag === 'speech_highlight' ? 'bg-gray-900 text-white border border-gray-800' :
                                tag === 'combined' ? 'bg-gray-50 text-gray-700 border border-gray-300' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                            
                            <Tooltip
                              content="Delete clip"
                              position="top"
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteClip(clip.id);
                                }}
                                className="p-1 text-gray-400 hover:text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      </Link>
                    );
                    });
                  })()}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <Play className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">No highlight clips were generated from this video.</p>
                <p className="text-sm text-gray-500 mb-4">
                  Try adjusting the analysis sensitivity or regenerating clips with different settings.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                  <button
                    onClick={manualStartAnalysis}
                    disabled={autoAnalyzing}
                    className="btn-secondary flex items-center"
                  >
                    {autoAnalyzing ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Regenerating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Regenerate Clips
                      </>
                    )}
                  </button>
                  <button
                    onClick={manualStartAnalysisWithRandomization}
                    disabled={autoAnalyzing}
                    className="btn-primary flex items-center"
                    title="Generate clips with randomized analysis for different results"
                  >
                    <Shuffle className="h-4 w-4 mr-2" />
                    Generate Different Clips
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
      
      {/* Guest Signup Prompt */}
      <GuestSignupPrompt
        isOpen={showGuestSignupPrompt}
        onClose={() => setShowGuestSignupPrompt(false)}
        onSignup={() => setShowAuthPrompt(true)}
        clipCount={clips.length}
      />
      
      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <AuthPrompt
          onClose={() => setShowAuthPrompt(false)}
          title="Create Your Free Account"
          message="Sign up to save your clips permanently and unlock unlimited AI-powered video analysis"
        />
      )}
    </div>
  );
};

export default VideoDetails; 